import React, { RefObject, useEffect, useRef, useState } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import {
  DEFAULT_TEAM_BOUNCE_LINK,
  TOURNAMENT_QUICK_CREATE_PAGE,
  getEventUrl,
} from 'constants/pages';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import { useGetGroupPageContentByGroupSlugLazyQuery } from 'types/generated/client';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { formatDateRange } from 'utils/shared/date/datesToDateRange';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useModal } from 'hooks/useModal';
import GalleryLeft from 'svg/GalleryLeft';
import GalleryRight from 'svg/GalleryRight';
import Globe from 'svg/Globe';
import InstagramIcon from 'svg/InstagramIcon';
import Location from 'svg/Location';
import Star from 'svg/Star';
import { ButtonLink } from 'components/Button';
import EmptyAction from 'components/EmptyAction';
import Footer from 'components/Footer';
import Link from 'components/Link';
import CardCoach from 'components/cards/CardCoach';
import CardTournament from 'components/cards/CardTournament';
import CardVenue from 'components/cards/CardVenue';
import ModalGroup from 'components/modals/ModalGroup';
import ModalShare from 'components/modals/ModalShare';
import TopNav from 'components/nav/TopNav/TopNav';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import FollowUs from './FollowUs';
import GroupPageContent from './GroupPageContent';
import GroupPageMob from './GroupPageMob';
import JoinGroup from './JoinGroup';
import RecentCommentsSection from './RecentCommentsSection';
import { GroupPageProps, Items } from './types';

const DEFAULT_GROUP_IMAGE = '/images/app/default_group_image.png';

export default function GroupsPage({ tournaments, group, jsonLd }: GroupPageProps) {
  const scrollContainerRefEvents = useRef<HTMLDivElement>(null);
  const scrollContainerRefCoaches = useRef<HTMLDivElement>(null);
  const scrollContainerRefCourts = useRef<HTMLDivElement>(null);
  const { centerLatitude, centerLongitude } = useGeoLocation();
  const { openSignupModal, ModalLogin, ModalSignup } = useAuthModals();

  const [getGroupPageContentByGroupSlugLazyQuery, { data: getUpdatedGroupPageData }] =
    useGetGroupPageContentByGroupSlugLazyQuery();

  const {
    isOpen: isJoinGroupOpen,
    openModal: openJoinGroupModal,
    closeModal: closeJoinGroupModal,
  } = useModal();

  const {
    openModal: openShareModal,
    closeModal: closeShareModal,
    isOpen: isShareModalOpen,
  } = useModal();

  const groupData = group;

  const decoratedTournaments = React.useMemo(() => {
    let processedTournaments = (tournaments || []).map((tournament) => {
      return {
        ...tournament,
        // startTimestamp: new Date(tournament.startDateTime).getTime(),
        displayDate: formatDateRange(tournament.startDateTime, tournament.endDateTime),
        distance: Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: tournament.latitude,
              longitude: tournament.longitude,
            },
            coord2: {
              latitude: centerLatitude,
              longitude: centerLongitude,
            },
            unit: 'miles',
          }),
        ),
      };
    });

    return processedTournaments;
  }, [tournaments, centerLatitude, centerLongitude]);

  const decoratedCoaches = React.useMemo(() => {
    let processCoaches = (
      getUpdatedGroupPageData?.groups?.[0].members ||
      groupData?.members ||
      []
    ).map((coach) => {
      return {
        ...coach,
        distance: Math.round(
          calculateHaversineDistance({
            coord1: {
              latitude: coach?.userProfile?.geometry?.coordinates[1],
              longitude: coach?.userProfile?.geometry?.coordinates[0],
            },
            coord2: {
              latitude: centerLatitude,
              longitude: centerLongitude,
            },
            unit: 'miles',
          }),
        ),
      };
    });

    // Sort coaches by distance
    processCoaches.sort((a, b) => a.distance - b.distance);

    return processCoaches;
  }, [groupData?.members, centerLatitude, centerLongitude, getUpdatedGroupPageData]);

  if (!groupData || !groupData.title) {
    return null;
  }

  const groupActionUrl = groupData?.actionUrl;

  const items: Items[] = [
    {
      svg: (
        <Location
          className="mr-1 h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
          viewBox="0 0 20 20"
        />
      ),
      text: groupData?.city?.name || '',
    },
    {
      svg: (
        <Star
          className="mr-1 h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
          viewBox="0 0 20 20"
        />
      ),
      text: groupData?.directorName || '',
    },
    {
      svg: (
        <Globe
          className="mr-1 h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
          viewBox="0 0 20 20"
        />
      ),
      text: groupData?.websiteUrl || '',
    },
    {
      svg: (
        <InstagramIcon
          className="ml-0.5 mr-1 h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
          viewBox="0 0 20 20"
        />
      ),
      text: groupData?.instagramUrl || '',
    },
  ];

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const shareAction = () => {
    openShareModal();
    if (navigator.share) {
      navigator
        .share({
          title: `Share Groups on Bounce`,
          text: `More details at ${shareUrl}`,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  const createScrollHandlers = (
    ref: RefObject<HTMLDivElement>,
    setIsScrolledToStart: React.Dispatch<React.SetStateAction<boolean>>,
    setIsScrolledToEnd: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const scrollLeft = () => {
      if (ref.current) {
        ref.current.scrollBy({
          left: -360,
          behavior: 'smooth',
        });
      }
    };

    const scrollRight = () => {
      if (ref.current) {
        ref.current.scrollBy({
          left: 360,
          behavior: 'smooth',
        });
      }
    };

    const handleScroll = () => {
      if (ref.current) {
        const { scrollLeft, scrollWidth, clientWidth } = ref.current;
        setIsScrolledToStart(scrollLeft === 0);
        setIsScrolledToEnd(scrollLeft + clientWidth >= scrollWidth);
      }
    };

    return { scrollLeft, scrollRight, handleScroll };
  };

  const refetchData = () => {
    getGroupPageContentByGroupSlugLazyQuery({
      variables: {
        groupId: groupData?.id,
      },
    });
  };

  const Wrapper = ({
    children,
    scrollContainerRef,
  }: {
    children: React.ReactNode;

    scrollContainerRef: React.RefObject<HTMLDivElement>;
  }) => {
    const [isScrolledToStart, setIsScrolledToStart] = useState(true);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
    const { scrollLeft, scrollRight, handleScroll } = createScrollHandlers(
      scrollContainerRef,
      setIsScrolledToStart,
      setIsScrolledToEnd,
    );

    useEffect(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
      }

      return () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.removeEventListener('scroll', handleScroll);
        }
      };
    }, [scrollContainerRef, handleScroll]);

    return (
      <div className="relative">
        {!isScrolledToStart && (
          <button
            onClick={scrollLeft}
            className="absolute top-1/3 z-30 -translate-y-1/2 cursor-pointer rounded-full bg-color-bg-lightmode-invert p-3 opacity-50 dark:bg-color-bg-darkmode-invert xs:left-[5%] md:left-[1%]"
          >
            <GalleryLeft className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="follow-scrollbar w-full max-w-full overflow-x-auto scroll-smooth"
        >
          {children}
        </div>
        {!isScrolledToEnd && (
          <button
            onClick={scrollRight}
            className="absolute top-1/3 z-30 -translate-y-1/2 cursor-pointer rounded-full bg-color-bg-lightmode-invert p-3 text-color-text-lightmode-invert opacity-50  dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert xs:right-[5%] md:right-[1%]"
          >
            <GalleryRight className="h-5 w-5 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
          </button>
        )}
      </div>
    );
  };

  const handleMailtoClick = () => {
    const mailtoLink = `mailto:${!groupActionUrl ? DEFAULT_TEAM_BOUNCE_LINK : group?.contactEmail
      }?subject=Contact Request for ${groupData?.title
      } Community&body=Hey Team,%0D%0A%0D%0AI'm trying to message a community lead on the platform but need some help.%0D%0A%0D%0AThanks!`;
    window.open(mailtoLink, '_blank');
  };

  return (
    <>
      <Head
        title={`${group?.title ? `${group?.title} | ` : ''}Pickleball Group`}
        description={`Join the ${group?.title} pickleball group in ${groupData?.city?.name}. Create round robins, share photos, organize open plays, and more.`}
      />
      <PopppinsFont />
      <TopNav
        shouldHideNavigation={false}
        shouldShowMobile
        shouldLinkToAuthPage={false}
        shouldShowAdditionalLinks={false}
        shouldShowStartAction
        isBlur
      />
      <div className="hidden px-4 pt-4 md:px-ds-2xl"></div>
      {/* Breadcrumbs */}
      <div className="hidden flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        <div className="relative flex w-full flex-col justify-between border-b border-color-border-input-lightmode px-4 pb-4 dark:border-color-border-input-darkmode md:flex-row md:items-center md:px-ds-2xl"></div>
      </div>
      {/* Main Content */}
      <main className="mx-auto h-full w-full max-w-[70rem]  bg-color-bg-lightmode-primary px-4 dark:bg-color-bg-darkmode-primary xs:pb-4 sm:px-8 md:pb-16">
        <div className="flex h-full w-full border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode xs:pb-6 md:mt-10 md:flex-row-reverse md:gap-8 md:pb-8">
          {/* Right - Sidebar */}
          <div className="sticky top-24 h-full shrink-0 self-start bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary xs:hidden md:block md:w-[18rem] lg:w-[22rem]">
            <FollowUs
              group={group}
              items={items}
              handleMailtoClick={handleMailtoClick}
              fetchData={refetchData}
              clickedSignUp={() => openSignupModal()}
            />
          </div>

          {/* Desktop content */}
          <div className="min-w-0 flex-1 xs:hidden md:block">
            <GroupPageContent
              shareAction={shareAction}
              group={groupData}
              imageUrl={groupData?.profilePhotoProviderUrl || DEFAULT_GROUP_IMAGE}
            />
          </div>

          {/* Mobile version */}
          <div className="flex h-full w-full overflow-x-hidden xs:block md:hidden">
            <GroupPageMob
              items={items}
              group={groupData}
              handleMailtoClick={handleMailtoClick}
              DEFAULT_GROUP_IMAGE={DEFAULT_GROUP_IMAGE}
              clickedSignUp={() => openSignupModal()}
            />
          </div>
        </div>

        <RecentCommentsSection
          groupId={groupData.id}
          groupSlug={groupData.slug}
          group={groupData}
          fetchData={refetchData}
          openJoinModal={openJoinGroupModal}
          openSignupModal={openSignupModal}
        />

        {/* Events Section */}
        <div className="mx-auto mt-ds-2xl max-w-7xl">
          <div className="relative mb-4 flex items-center justify-between leading-none">
            <h2 className="typography-product-heading">Upcoming events</h2>
          </div>
          <div>
            <div className="border-b border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
              <>
                {decoratedTournaments && decoratedTournaments.length > 0 ? (
                  <div className="flex flex-col gap-10">
                    <Wrapper scrollContainerRef={scrollContainerRefEvents}>
                      <div className="flex gap-x-6">
                        {decoratedTournaments.map((tournament, tournamentIndex) => {
                          return (
                            <div key={tournamentIndex} className="w-[22rem] min-w-[21rem]">
                              <Link
                                key={tournament.id}
                                className="block w-full"
                                href={getEventUrl(tournament.id)}
                              >
                                <CardTournament
                                  key={tournament.id}
                                  listHoveredTournamentId={null}
                                  setListHoveredTournamentId={() => { }}
                                  tournamentId={tournament.id}
                                  coverImageUrl={
                                    tournament.coverImagePath
                                      ? getImageUrl({
                                        url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                                        path: tournament.coverImagePath,
                                      })
                                      : ''
                                  }
                                  organizerImageUrl={
                                    tournament.organizerImagePath
                                      ? getImageUrl({
                                        url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                                        path: tournament.organizerImagePath,
                                      })
                                      : ''
                                  }
                                  title={tournament.title}
                                  registrationClosedAt={tournament.registrationClosedAt}
                                  startTimestamp={tournament.startDateTime}
                                  status={tournament.status}
                                  sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                                  sourceRegistrationCount={tournament.sourceRegistrationCount}
                                  registrationCount={
                                    tournament.registrationsAggregate?.aggregate?.count || 0
                                  }
                                  isExternal={tournament.isExternal}
                                  displayDate={tournament.displayDate}
                                  displayLocation={
                                    tournament.city?.name &&
                                      tournament.city?.countrySubdivision?.code
                                      ? `${tournament.city?.name}, ${tournament.city?.countrySubdivision?.code}`
                                      : tournament.displayLocation
                                  }
                                  distance={tournament.distance}
                                />
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    </Wrapper>
                    <div className="flex xs:justify-center md:justify-start">
                      <ButtonLink
                        href={`${TOURNAMENT_QUICK_CREATE_PAGE}?groupId=${groupData.id}`}
                        variant="brand"
                        size="md"
                        className="h-10 xs:w-40 md:w-40	"
                      >
                        + Create
                      </ButtonLink>
                    </div>
                  </div>
                ) : (
                  <EmptyAction
                    description="No events scheduled nearby"
                    actionUrl={`${TOURNAMENT_QUICK_CREATE_PAGE}?groupId=${groupData.id}`}
                    actionText="Create"
                  />
                )}
              </>
            </div>
          </div>
        </div>
        {/* Coaches Section */}
        <div className="mx-auto mt-ds-2xl max-w-7xl">
          <div className="relative mb-4 flex items-center justify-between leading-none">
            <h2 className="typography-product-heading">Coaches</h2>
          </div>
          <div>
            <div className="border-b border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
              {decoratedCoaches && decoratedCoaches?.length > 0 ? (
                <>
                  <Wrapper scrollContainerRef={scrollContainerRefCoaches}>
                    <div className="flex gap-x-6">
                      {decoratedCoaches.map((coach, coachesIndex) => {
                        return (
                          <div key={coachesIndex} className="w-[22rem] min-w-[21rem]">
                            <CardCoach
                              key={coach?.userProfile?.id}
                              id={coach?.userProfile?.id}
                              slug={coach?.userProfile?.username || coach?.userProfile?.id}
                              photoUrl={
                                coach?.userProfile?.profileImageProviderUrl ||
                                EMPTY_COACH_AVATAR_SRC
                              }
                              certificationImages={[]}
                              fullName={toTitleCase(coach?.userProfile?.fullName || '') || ''}
                              price={coach?.userProfile?.priceUnitAmountCoachDefault || 0}
                              distance={coach.distance}
                              isBookButton={true}
                              currency={
                                coach?.userProfile?.stripeMerchantCurrency ||
                                coach?.userProfile?.currencyCoachDefault ||
                                CURRENCY_USD
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Wrapper>
                </>
              ) : (
                <EmptyAction
                  description="This group doesn’t have any coaches yet."
                  actionHandler={() => openShareModal()}
                  actionText="Invite a Coach"
                />
              )}
            </div>
          </div>
        </div>

        {/* Courts Section */}
        <div className="mx-auto mt-ds-2xl max-w-7xl">
          <div className="relative mb-4 flex items-center justify-between leading-none">
            <h2 className="typography-product-heading">Courts</h2>
          </div>

          <div className="pb-ds-1xl border-color-border-input-lightmode dark:border-color-border-input-darkmode">
            {groupData && groupData?.venues?.length > 0 ? (
              <>
                <div key={groupData?.venues[0]?.id}>
                  <Wrapper scrollContainerRef={scrollContainerRefCourts}>
                    <div className="flex gap-x-6">
                      {groupData?.venues.map((venue, venueIndex) => (
                        <div key={venueIndex} className="w-[22rem] min-w-[21rem]">
                          <CardVenue
                            key={venue?.venue?.id}
                            id={venue?.venue?.id}
                            slug={venue?.venue?.slug}
                            isLink={true}
                            imageUrl={venue?.venue?.images[0]?.url}
                            title={venue?.venue?.title}
                            addressString={venue?.venue?.addressString}
                            accessType={venue?.venue?.accessType}
                            totalCourtCount={
                              venue?.venue?.indoorCourtCount + venue?.venue?.outdoorCourtCount
                            }
                            pickleballNets={venue?.venue?.pickleballNets}
                          />
                        </div>
                      ))}
                    </div>
                  </Wrapper>
                </div>
              </>
            ) : (
              <EmptyAction
                description="No courts available nearby"
                actionUrl={TOURNAMENT_QUICK_CREATE_PAGE}
                actionText="Register a court"
              />
            )}
          </div>
        </div>
      </main>
      {/* Join Group */}
      <header className="z-20 h-[5.5rem] w-full items-center justify-center border-t border-color-border-input-lightmode bg-color-bg-lightmode-primary px-4 pb-20 pt-11 dark:bg-color-bg-darkmode-primary xs:flex xs:gap-4 md:hidden md:gap-6">
        <div className="flex w-full items-center">
          <JoinGroup
            actionUrl={groupActionUrl}
            actionHandler={openJoinGroupModal}
            groupId={groupData.id}
            fetchData={refetchData}
            clickedSignUp={() => openSignupModal()}
          />
        </div>
      </header>
      <ModalGroup
        isOpen={isJoinGroupOpen}
        closeModal={closeJoinGroupModal}
        title="Join the group"
        description={`This organizer has not provided a link to join this group yet. We'll let them know you want to join.`}
      />
      <ModalShare
        isOpen={isShareModalOpen}
        closeModal={closeShareModal}
        shareUrl={shareUrl}
        title="Share this link"
      />
      <Footer />
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      <ModalLogin isShowSignupLink />
      <ModalSignup isShowLoginLink />
    </>
  );
}
